import React, { useContext, useState } from 'react';
import { AppContext } from '../AppContext';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Share2, Heart, Bookmark } from 'lucide-react';
import { showToast } from '../sweetAlert';
import './BlogPage.css';

const BlogPage = () => {
  const { blogs, t, lang } = useContext(AppContext);
  const isMr = lang === 'mr';
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const getLocalizedContent = (blog, field) => {
    if (lang === 'mr' && blog[`${field}Mr`]) {
      return blog[`${field}Mr`];
    }
    return blog[field];
  };

  if (selectedBlog) {
    return (
      <motion.div
        className="full-blog-article-page"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="article-top-bar">
          <div className="article-container">
            <button
              className="btn-back-journal"
              onClick={() => {
                setSelectedBlog(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <ArrowLeft size={18} /> {t.backToJournal || (isMr ? 'जर्नलकडे परत जा' : 'Back to Journal')}
            </button>
            <div className="article-social-actions">
              <button className={`btn-art-action ${liked ? 'active' : ''}`} onClick={() => setLiked(!liked)}>
                <Heart size={18} fill={liked ? '#d4af37' : 'none'} color={liked ? '#d4af37' : '#3b2c24'} />
              </button>
              <button className={`btn-art-action ${bookmarked ? 'active' : ''}`} onClick={() => setBookmarked(!bookmarked)}>
                <Bookmark size={18} fill={bookmarked ? '#d4af37' : 'none'} color={bookmarked ? '#d4af37' : '#3b2c24'} />
              </button>
              <button
                className="btn-art-action"
                onClick={() => {
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(window.location.href);
                  }
                  showToast(isMr ? 'लेखाची लिंक क्लिपबोर्डवर कॉपी केली!' : 'Article link copied to clipboard!');
                }}
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="article-main-container">
          <div className="article-two-col-grid">
            {/* Left Column: Image */}
            <div className="article-left-col">
              <div className="article-sticky-image-wrap">
                <motion.img
                  src={selectedBlog.image}
                  alt={selectedBlog.title}
                  initial={{ scale: 0.98, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                />
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="article-right-col">
              <motion.span className="article-category-tag" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {t.gdrLuxuryEditorial || (isMr ? 'जीडीआर लक्झरी संपादकीय' : 'GDR LUXURY EDITORIAL')}
              </motion.span>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                {getLocalizedContent(selectedBlog, 'title')}
              </motion.h1>

              <div className="article-meta-row">
                <div className="author-badge">
                  <div className="author-avatar">{selectedBlog.author ? selectedBlog.author[0] : 'G'}</div>
                  <div>
                    <strong>{selectedBlog.author || (isMr ? 'जीडीआर संपादकीय टीम' : 'GDR Editorial Team')}</strong>
                    <span>{isMr ? 'मास्टर ज्वेलर्स आणि स्टायलिस्ट्स' : 'Master Jewellers & Stylists'}</span>
                  </div>
                </div>
                <div className="article-stats">
                  <span><Calendar size={14} /> {selectedBlog.date}</span>
                  <span><Clock size={14} /> {selectedBlog.readTime}</span>
                </div>
              </div>

              <div className="article-body-content">
                <p className="article-lead">{getLocalizedContent(selectedBlog, 'excerpt')}</p>

                <div className="article-divider"><span>✦</span></div>

                {(getLocalizedContent(selectedBlog, 'content') || '').split('\n\n').map((para, idx) => (
                  <p key={idx} className="article-paragraph">{para}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="blog-page">
      <section className="bp-concept-banner">
        <div className="bp-concept-bg">
          <img src="images/b banner.png" alt="GDR Editorial Journal Banner" />
          <div className="bp-concept-overlay"></div>
        </div>
        <div className="bp-concept-content">

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            {t.jewelleryStories || (isMr ? 'दागिन्यांच्या कथा आणि डिझाइन प्रेरणा' : 'Jewellery Stories & Design Inspiration')}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            {t.exploreCraftsmanship || (isMr ? 'उत्कृष्ट दागिन्यांची कलाकुसर, स्टाईल मार्गदर्शक आणि ऐतिहासिक महत्त्व एक्सप्लोर करा.' : 'Explore the craftsmanship, style guides, and historical significance behind fine jewellery.')}
          </motion.p>
        </div>
      </section>

      <section className="bp-grid-section">
        <div className="bp-container">
          <div className="bp-grid">
            {blogs.map((b, i) => (
              <motion.article
                key={b.id}
                className="blog-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -8 }}
                onClick={() => {
                  setSelectedBlog(b);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <div className="blog-card-img-wrap">
                  <img src={b.image} alt={b.title} />
                  <span className="blog-read-time">{b.readTime}</span>
                </div>
                <div className="blog-card-body">
                  <span className="blog-date"><Calendar size={13} /> {b.date}</span>
                  <h3>{getLocalizedContent(b, 'title')}</h3>
                  <p>{getLocalizedContent(b, 'excerpt')}</p>
                  <span className="blog-read-more">{isMr ? 'संपूर्ण लेख वाचा →' : 'Read Full Article →'}</span>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
